import { apiClient, API_PRX } from '@indipia/shared'
import type { ApiResponse } from '@indipia/shared-types'
import type { CustReqAdditionalDeliveryInput, CustReqAdditionalDeliveryResult, CustReqCreateInput, CustReqItem, CustReqSrcOption, CustReqUpdateInput } from './types'

// 고객 요청(S16) 호출 — sv-worklog CustReqController(K0 계약 2장). 요청 헤더(X-Comp-Id·X-User-Id)는 게이트웨이가 주입한다.
//  GET    /worklog/customer-requests?taskId=                       : 공정별 목록(전 상태)
//  POST   /worklog/customer-requests                               : 등록(multipart, 사진 포함)
//  PATCH  /worklog/customer-requests/{id}                          : 내용·받은 경로 수정(접수 건만)
//  DELETE /worklog/customer-requests/{id}                          : 삭제(접수 건만)
//  POST   /worklog/customer-requests/{id}/photos                   : 사진 추가(multipart)
//  DELETE /worklog/customer-requests/{id}/photos/{photoId}         : 사진 1장 삭제
//  POST   /worklog/customer-requests/additional-delivery           : 반장님께 추가 전달(미전달 건 선택)
//  GET    /indipia/codes?codeGroupCode=ZEU_CUST_REQ_SRC            : 받은 경로 선택지(공통코드)

const BASE = `${API_PRX.WORKLOG}/customer-requests`
export const CUST_REQ_SRC_GROUP = 'ZEU_CUST_REQ_SRC'

function need<T>(v: T | null | undefined, what: string): T {
  // 필수 응답 값이 없으면 기본값으로 채우지 않고 오류
  if (v == null) throw new Error(`${what} 응답이 비어 있음`)
  return v
}

function needItem(v: CustReqItem | null | undefined): CustReqItem {
  const d = need(v, '고객 요청')
  if (!Array.isArray(d.photos)) throw new Error('고객 요청 형식 오류')
  return d
}

/** 서버 오류 → 화면 문구. 서버 message 가 있으면 그대로, 없으면 fallback(저장·삭제 실패 토스트용) */
export function custReqErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message
  return typeof msg === 'string' && msg.trim() !== '' ? msg : fallback
}

export const custReqApi = {
  async list(taskId: number, signal?: AbortSignal): Promise<CustReqItem[]> {
    const res = await apiClient.get<ApiResponse<{ items: CustReqItem[]; totalCount: number }>>(BASE, { params: { taskId }, signal })
    const d = need(res.data?.data, '고객 요청 목록')
    if (!Array.isArray(d.items)) throw new Error('고객 요청 목록 형식 오류')
    return d.items
  },

  async create(input: CustReqCreateInput): Promise<CustReqItem> {
    // Content-Type 은 직접 지정하지 않는다(axios 가 multipart boundary 를 붙임)
    const form = new FormData()
    form.append('siteId', String(input.siteId))
    form.append('scheduleId', String(input.scheduleId))
    form.append('taskId', String(input.taskId))
    form.append('requestKey', input.requestKey)
    form.append('content', input.content)
    form.append('srcCode', input.srcCode)
    input.files.forEach(f => form.append('photoFiles', f))
    const res = await apiClient.post<ApiResponse<CustReqItem>>(BASE, form)
    return needItem(res.data?.data)
  },

  async update(custReqId: number, input: CustReqUpdateInput): Promise<CustReqItem> {
    const res = await apiClient.patch<ApiResponse<CustReqItem>>(`${BASE}/${custReqId}`, input)
    return needItem(res.data?.data)
  },

  async remove(custReqId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${custReqId}`)
  },

  async addPhotos(custReqId: number, files: File[]): Promise<CustReqItem> {
    const form = new FormData()
    files.forEach(f => form.append('photoFiles', f))
    const res = await apiClient.post<ApiResponse<CustReqItem>>(`${BASE}/${custReqId}/photos`, form)
    return needItem(res.data?.data)
  },

  async removePhoto(custReqId: number, photoId: number): Promise<CustReqItem> {
    const res = await apiClient.delete<ApiResponse<CustReqItem>>(`${BASE}/${custReqId}/photos/${photoId}`)
    return needItem(res.data?.data)
  },

  /** 반장님께 추가 전달(K1 A2) — 발송 접수 성공 시에만 전달됨으로 바뀜. 실패(409·503)는 상태 불변 */
  async additionalDelivery(input: CustReqAdditionalDeliveryInput): Promise<CustReqAdditionalDeliveryResult> {
    const res = await apiClient.post<ApiResponse<CustReqAdditionalDeliveryResult>>(`${BASE}/additional-delivery`, input)
    const d = need(res.data?.data, '추가 전달 결과')
    if (!Array.isArray(d.items) || !Array.isArray(d.recipients)) throw new Error('추가 전달 결과 형식 오류')
    return d
  },

  /** 받은 경로 선택지 — 폴백 값 없음(실패하면 오류) */
  async listSrcOptions(signal?: AbortSignal): Promise<CustReqSrcOption[]> {
    const res = await apiClient.get<ApiResponse<Array<{ codeValue: string; codeName: string }>>>(
      `${API_PRX.INDIPIA}/codes`, { params: { codeGroupCode: CUST_REQ_SRC_GROUP }, signal },
    )
    const d = need(res.data?.data, '받은 경로 코드')
    if (!Array.isArray(d)) throw new Error('받은 경로 코드 형식 오류')
    return d.map(c => ({ value: c.codeValue, label: c.codeName }))
  },
}
